/**
 * @module RequestsStatusSummary
 * @purpose Two-dimension status summary grid for CollectionRequestsView -- a package-status row and a return-status row (PRP §2 C10).
 * @depends collection-request-status.labels, StatusCountResponse/DimensionStatusCount types
 * @dependedOnBy CollectionRequestsView
 *
 * @rules
 * - State: none -- pure presentational component
 * - Errors: n/a
 */

import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type { DimensionStatusCount, StatusCountResponse } from '../../../types/case-container'
import {
  packageStatusLabelOrRaw,
  returnStatusLabelOrRaw,
} from '../../../components/collections/collection-request-status-badge/collection-request-status.labels'

interface StatusCountRowProps {
  title: string
  counts: DimensionStatusCount[]
  labelFor: (status: string) => string
}

function StatusCountRow({ title, counts, labelFor }: StatusCountRowProps): React.ReactElement | null {
  if (counts.length === 0) return null
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">{title}</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        {counts.map((statusCount) => (
          <div
            key={statusCount.status}
            className="rounded-lg border border-slate-200 p-3 text-center dark:border-slate-700"
          >
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {statusCount.count}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {labelFor(statusCount.status)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function buildPackageLabelFor(t: TFunction<'cases'>): (status: string) => string {
  return (status: string) => packageStatusLabelOrRaw(t, status)
}

function buildReturnLabelFor(t: TFunction<'cases'>): (status: string) => string {
  return (status: string) => returnStatusLabelOrRaw(t, status)
}

/**
 * Two-row status summary -- package-build counts, then return-progress counts.
 *
 * @behaviour Renders nothing when both dimensions have zero entries; the
 * return-status row excludes the 'none' bucket (matches the badge display
 * convention -- 'none' carries no information worth a tile).
 * @errors n/a -- pure display, no data fetching
 */
export function StatusSummaryGrid({
  statusCounts,
}: {
  statusCounts: StatusCountResponse
}): React.ReactElement | null {
  const { t } = useTranslation('cases')
  const returnCounts = statusCounts.returnStatus.filter((entry) => entry.status !== 'none')
  if (statusCounts.packageStatus.length === 0 && returnCounts.length === 0) return null
  return (
    <div className="space-y-3">
      <StatusCountRow
        title={t('collections.packageStatusRowTitle')}
        counts={statusCounts.packageStatus}
        labelFor={buildPackageLabelFor(t)}
      />
      <StatusCountRow
        title={t('collections.returnStatusRowTitle')}
        counts={returnCounts}
        labelFor={buildReturnLabelFor(t)}
      />
    </div>
  )
}
